import {
  useGetAllProgress,
  useGetIcbtClaims,
  useGetIcbtRequests,
} from "api/useIcbtQueries";
import { marketFromCountryId } from "helpers/getCountryCode";
import { useQueryErrorLogger } from "hooks";
import { useGetAllProgramsAndTreatmentsQuery } from "hooks/icbt/useGetAllProgramsAndTreatmentsQuery";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { styled } from "styled-components";
import { ProgressBreadcrumbs } from "components/icbt/assignIcbtModal/progressBreadcrumbs";
import { Typography } from "components/typography";
import { useIcbtLanguage } from "../hooks/useTreatment";
import { useAssignIcbt } from "./assignIcbtContext";
import { RouterOutput } from "@mindlercare/mindlerapi-client";
import { Button, Icon } from "@mindlercare/ui-web";
import { IcbtSelector } from "components/icbt/assignIcbtModal/icbtSelector";
import { useDemo } from "contexts/DemoContext";
import CONSTANTS from "core/constants";
import { useSelectedMarket } from "core/selectors/marketSelectors";
import { useUserHasRole } from "../../../hooks/userHooks";
import { PossiblyErrors, mapTreatmentData } from "./common";
export { ModalContent } from "./assignIcbt.styles";

export const FixedContainer = styled.div`
  position: fixed;
  inset: 0;
  width: 90%;
  height: 90%;
  margin: auto;
  overflow-x: hidden;
`;

export const ModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  height: 100%;
  padding: 24px 40px 40px 40px;
  background-color: ${({ theme }) => theme.COLORS.WHITE};
`;

export const ModalHeader = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
`; // padding balances a bit

const HeaderOptions = styled.div`
  width: 100%;
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 24px;
`;

const CloseIconButton = styled(Button)`
  border: none;

  &:hover,
  &:focus {
    background-color: transparent;
    border: none;
  }
`;

const SpecialStyledTypographyToMakeDesignHappy = styled(Typography)`
  font-size: ${({ theme }) => theme.FONT.SIZE.EXTRA_LARGE};
  font-weight: ${({ theme }) => theme.FONT.WEIGHT.MEDIUM};
`;

type GetPatientByUserIdResult = RouterOutput["patients"]["getByUserId"];

export const AssignIcbt = ({
  patient,
  closeModal,
  handleOpenConfirm,
  feedbackDateFeatureEnabledForPatient = false,
}: {
  patient?: GetPatientByUserIdResult;
  closeModal?: () => void;
  handleOpenConfirm?: () => void;
  feedbackDateFeatureEnabledForPatient?: boolean;
}) => {
  const { dispatch } = useAssignIcbt();

  const icbtLanguage = useIcbtLanguage();
  const [selectedLanguage, setSelectedLanguage] = useState(icbtLanguage);
  const isPreviewOnly = !closeModal && !handleOpenConfirm;

  const { data: icbtClaims } = useGetIcbtClaims(
    {
      userId: patient?.userId,
      marketISO2: marketFromCountryId(patient?.countryId),
    },
    { enabled: !isPreviewOnly },
  );

  const { t } = useTranslation();
  const isPsychologist = useUserHasRole("Psychologist");
  const market = useSelectedMarket();

  const [progressStep, setProgressStep] = useState<number>(0);

  const {
    data: programsAndTreatments,
    isLoading: programsAndTreatmentsIsLoading,
    error: programsAndTreatmentsError,
  } = useGetAllProgramsAndTreatmentsQuery(selectedLanguage?.codeISO2);

  const {
    data: icbtRequests,
    isLoading: icbtRequestsIsLoading,
    error: icbtRequestsError,
  } = useGetIcbtRequests(patient?.userId);

  const {
    data: allProgress,
    isLoading: allProgressIsLoading,
    error: allProgressError,
  } = useGetAllProgress(patient?.userId?.toString());

  // Check for errors, derive error status and log if there are errors
  const possiblyErrors: PossiblyErrors = {
    programsAndTreatmentsError,
    icbtRequestsError,
    allProgressError,
  };

  const isError = useQueryErrorLogger(
    possiblyErrors,
    "Failed to load data in assign icbt modal",
    {
      team: "dna",
      tag: "assignIcbt",
      patientUserId: patient?.userId,
    },
  );

  // Derive loading status
  const isLoading =
    programsAndTreatmentsIsLoading ||
    icbtRequestsIsLoading ||
    allProgressIsLoading;

  // Derive data
  const treatments = useMemo(() => {
    return mapTreatmentData(
      programsAndTreatments,
      icbtRequests,
      allProgress,
      t,
    );
  }, [programsAndTreatments, allProgress, icbtRequests, t]);

  const isDemo = useDemo();

  const handleSelectTreatment = useCallback(
    (treatmentId?: string) => {
      dispatch({ type: "setSelectedTreatmentId", payload: treatmentId });
    },
    [dispatch],
  );

  const closeAssignIcbtModal = useCallback(() => {
    closeModal?.();
  }, [closeModal]);

  return (
    <ModalContainer>
      <ModalHeader>
        <HeaderOptions>
          <SpecialStyledTypographyToMakeDesignHappy
            variant={isPreviewOnly ? "h1" : "h3"}
            data-testid="modalTitle"
            insensitive
          >
            {isPreviewOnly
              ? t("PREVIEW_ICBT_TITLE")
              : t("ASSIGN_ICBT_TITLE", {
                  patientName: isDemo
                    ? CONSTANTS.DEMO
                    : `${patient?.firstName} ${patient?.lastName}`,
                })}
          </SpecialStyledTypographyToMakeDesignHappy>

          <ProgressBreadcrumbs
            enabled={feedbackDateFeatureEnabledForPatient && !isPreviewOnly}
            breadcrumbs={[
              { title: t("SELECT_ICBT") },
              { title: t("ADD_FEEDBACK_DATE") },
            ]}
            activeIndex={progressStep}
          />

          {!isPreviewOnly ? (
            <CloseIconButton variant="icon" onClick={closeAssignIcbtModal}>
              <Icon.Close />
            </CloseIconButton>
          ) : null}
        </HeaderOptions>
      </ModalHeader>
      <IcbtSelector
        progressStep={progressStep}
        setProgressStep={setProgressStep}
        isLoading={isLoading}
        isPreviewOnly={isPreviewOnly}
        closeAssignIcbtModal={closeAssignIcbtModal}
        icbtClaims={icbtClaims}
        handleOpenConfirm={handleOpenConfirm}
        handleSelectTreatment={handleSelectTreatment}
        treatments={treatments}
        assigningEnabled={isPsychologist}
        isError={isError}
        possiblyErrors={possiblyErrors}
        feedbackDateFeatureEnabledForPatient={
          feedbackDateFeatureEnabledForPatient
        }
        setSelectedLanguage={setSelectedLanguage}
        selectedLanguage={selectedLanguage}
        languages={market?.languages}
        patientUserId={patient?.userId ? String(patient?.userId) : "null"}
      />
    </ModalContainer>
  );
};
